/**
 * One alternative investment's own page: its value and the report it comes
 * from, and every month — report months with the quarter's capital calls,
 * distributions and return; the months between shown as carried from the last
 * report, with no flows and no return of their own.
 */
import { altReportDateOf, altReportLabel, type AltInvestmentView } from '@agio/core/domain';
import type { PerformanceRow } from '@agio/core';
import { Card, EmptyState } from './Card.js';
import { cn, fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { AltBenchmarkNote } from '../altReports.js';

const fmtDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

export const alternativeHasData = (perf: PerformanceRow[]) => perf.some((p) => altReportDateOf(p.sheet_name));

export function AlternativeFundPanel({
  fundId,
  perf,
  investment,
}: {
  fundId: string;
  perf: PerformanceRow[];
  /** The investment's own details (manager, currency); null while loading. */
  investment: AltInvestmentView | null;
}) {
  const money = useMoney();
  const rows = perf.filter((p) => p.fund_id === fundId && altReportDateOf(p.sheet_name)).sort((a, b) => b.date.localeCompare(a.date));
  if (rows.length === 0) return <Card title="השקעה אלטרנטיבית"><EmptyState label="אין נתונים" /></Card>;
  const latest = rows[0];
  const latestAsOf = altReportDateOf(latest.sheet_name)!;

  return (
    <Card title="השקעה אלטרנטיבית">
      <div className="mb-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
        <span className="text-2xl font-semibold tabular-nums">{money(latest.asset_volume, fundId)}</span>
        <span className="text-muted-foreground">{altReportLabel(latestAsOf)}</span>
        {investment?.manager && <span className="text-muted-foreground">מנהל: <bdi>{investment.manager}</bdi></span>}
        <span className="text-muted-foreground">מתעדכן מדוחות רבעוניים</span>
      </div>
      <div className="overflow-x-auto">
        <table className="print-dense-table w-full min-w-[34rem] whitespace-nowrap text-sm">
          <thead>
            <tr className="border-b border-border text-right text-muted-foreground">
              <th className="py-2 pe-2 font-normal">חודש</th>
              <th className="py-2 pe-2 font-normal">שווי</th>
              <th className="py-2 pe-2 font-normal">קריאות לכסף</th>
              <th className="py-2 pe-2 font-normal">חלוקות</th>
              <th className="py-2 pe-2 font-normal">תשואה</th>
              <th className="py-2 pe-2 font-normal">מקור</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const asOf = altReportDateOf(r.sheet_name)!;
              const carried = asOf.slice(0, 7) !== r.date.slice(0, 7);
              return (
                <tr key={r.date} className={cn('border-b border-border/50', carried && 'text-muted-foreground')}>
                  <td className="py-2 pe-2 tabular-nums">{r.date.slice(5, 7)}/{r.date.slice(0, 4)}</td>
                  <td className={cn('py-2 pe-2 tabular-nums', !carried && 'font-medium')}>{money(r.asset_volume, fundId)}</td>
                  <td className="py-2 pe-2 tabular-nums">{!carried && r.deposits ? money(r.deposits, fundId) : '—'}</td>
                  <td className="py-2 pe-2 tabular-nums">{!carried && r.withdrawals ? money(r.withdrawals, fundId) : '—'}</td>
                  <td className={cn('py-2 pe-2 tabular-nums', !carried && r.monthly_return !== null && (r.monthly_return >= 0 ? 'text-success' : 'text-destructive'))}>
                    {carried ? '—' : fmtPct(r.monthly_return)}
                  </td>
                  <td className="py-2 pe-2">{carried ? `נגרר מדוח ${fmtDate(asOf)}` : `דוח ${fmtDate(asOf)}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-3 grid gap-1">
        <p className="text-xs text-muted-foreground">
          בין דוח לדוח השווי האחרון נגרר קדימה, בלי תשואה; תשואת הרבעון כולו נרשמת בחודש הדוח. קריאות לכסף נספרות כהפקדות וחלוקות כמשיכות.
        </p>
        <AltBenchmarkNote />
      </div>
    </Card>
  );
}
